import { Schema } from "effect";
import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas";

const FILE_PREVIEW_PATH_MAX_LENGTH = 4_096;

export const FILE_PREVIEW_MAX_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * `cwd` identifies the project/thread the request comes from — used to
 * resolve the project's `.taskin/config.json` glob override and to check the
 * caller has `p.thread.use` on the owning project. `path` is the absolute
 * filesystem path to preview; it is validated against the admin's global
 * glob allowlist union the project's override, not against `cwd`'s workspace
 * root (unlike `ProjectReadFileInput`, this is not a workspace-relative
 * read).
 */
export const FilePreviewInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  path: TrimmedNonEmptyString.check(Schema.isMaxLength(FILE_PREVIEW_PATH_MAX_LENGTH)),
});
export type FilePreviewInput = typeof FilePreviewInput.Type;

/**
 * Mirrors `ProjectReadFileResult` plus a `not-allowed` case for a path that
 * does not match any configured glob — kept as a distinct outcome rather
 * than a request-level failure so the client can render it the same way as
 * "missing" or "too-large", without a special error path.
 */
export const FilePreviewResult = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("text"),
    contents: Schema.String,
    mtimeMs: NonNegativeInt,
    sizeBytes: NonNegativeInt,
    languageId: Schema.NullOr(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    kind: Schema.Literal("missing"),
  }),
  Schema.Struct({
    kind: Schema.Literal("too-large"),
    sizeBytes: NonNegativeInt,
    maxBytes: NonNegativeInt,
  }),
  Schema.Struct({
    kind: Schema.Literal("binary"),
    sizeBytes: NonNegativeInt,
  }),
  Schema.Struct({
    kind: Schema.Literal("not-allowed"),
  }),
]);
export type FilePreviewResult = typeof FilePreviewResult.Type;

/**
 * The effective allowlist for a project, so the web client can tell which
 * file paths mentioned in a chat message are actually previewable before
 * turning any of them into a link. Without this the client would have to
 * guess from the shape of the text alone and would linkify every
 * path-looking token — including things that merely contain a slash.
 *
 * Same `p.thread.use` gate as `files.preview`: this only reveals which
 * locations are previewable, which anyone who can click a preview would
 * learn anyway by trying one.
 */
export const FilePreviewGlobsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type FilePreviewGlobsInput = typeof FilePreviewGlobsInput.Type;

export const FilePreviewGlobsResult = Schema.Struct({
  allowedGlobs: Schema.Array(TrimmedNonEmptyString),
});
export type FilePreviewGlobsResult = typeof FilePreviewGlobsResult.Type;
